/**
 * Audio utility functions for Web Audio API
 */

/**
 * 創建 AudioContext
 * @returns {AudioContext} 新的 AudioContext 實例
 */
export const createAudioContext = () => {
    return new (window.AudioContext || window.webkitAudioContext)();
};

/**
 * 載入音檔並解碼為 AudioBuffer
 * @param {AudioContext} audioContext - AudioContext 實例
 * @param {string} audioPath - 音檔路徑
 * @returns {Promise<AudioBuffer>} 解碼後的 AudioBuffer
 */
export const loadAudioFile = async (audioContext, audioPath) => {
    const response = await fetch(audioPath);
    if (!response.ok) {
        console.error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    return audioBuffer;
};

/**
 * 創建 Gain Node
 * @param {AudioContext} audioContext - AudioContext 實例
 * @param {number} volume - 音量值 (0-100)
 * @returns {GainNode} Gain Node
 */
export const createGainNode = (audioContext, volume = 100) => {
    const gainNode = audioContext.createGain();
    gainNode.gain.value = volume / 100;
    return gainNode;
};

/**
 * 創建 EQ Filter 節點
 * @param {AudioContext} audioContext - AudioContext 實例
 * @param {string} type - Filter 類型 ('highshelf' | 'peaking' | 'lowshelf')
 * @param {number} frequency - 頻率值
 * @param {number} Q - Q 值
 * @param {number} gain - 增益值 (0-100, 50 為中心值)
 * @returns {BiquadFilterNode} Filter Node
 */
export const createEQFilter = (audioContext, type, frequency, Q = 1, gain = 50) => {
    const filter = audioContext.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = Q;
    // 將 0-100 映射到 -12dB 到 +12dB
    filter.gain.value = (gain - 50) / 50 * 12;
    return filter;
};

/**
 * 創建完整的 EQ 鏈（High -> Mid -> Low -> Gain -> Destination）
 * @param {AudioContext} audioContext - AudioContext 實例
 * @param {Object} options - 配置選項
 * @param {number} options.volume - 音量值 (0-100)
 * @param {Object} options.eqValues - EQ 值 { high: number, mid: number, low: number }
 * @returns {Object} 包含所有節點的對象 { highFilter, midFilter, lowFilter, gainNode }
 */
export const createEQChain = (audioContext, options = {}) => {
    const { volume = 100, eqValues = { high: 50, mid: 50, low: 50 } } = options;

    // 創建 Gain Node
    const gainNode = createGainNode(audioContext, volume);

    // 創建 EQ Filters
    const highFilter = createEQFilter(audioContext, 'highshelf', 5000, 1, eqValues.high);
    const midFilter = createEQFilter(audioContext, 'peaking', 1000, 1, eqValues.mid);
    const lowFilter = createEQFilter(audioContext, 'lowshelf', 200, 1, eqValues.low);

    // 連接節點: high -> mid -> low -> gain -> destination
    highFilter.connect(midFilter);
    midFilter.connect(lowFilter);
    lowFilter.connect(gainNode);
    gainNode.connect(audioContext.destination);

    return {
        highFilter,
        midFilter,
        lowFilter,
        gainNode
    };
};

/**
 * 斷開所有音頻節點的連接
 * @param {Object} nodes - 包含節點的對象 { highFilter?, midFilter?, lowFilter?, gainNode? }
 */
export const disconnectAudioNodes = (nodes) => {
    const { highFilter, midFilter, lowFilter, gainNode } = nodes;
    
    try {
        if (highFilter) highFilter.disconnect();
        if (midFilter) midFilter.disconnect();
        if (lowFilter) lowFilter.disconnect();
        if (gainNode) gainNode.disconnect();
    } catch (e) {
        // 忽略斷開連接的錯誤
        console.warn('Error disconnecting audio nodes:', e);
    }
};

/**
 * 確保所有音頻節點都屬於同一個 AudioContext
 * 如果節點不存在或不屬於當前 AudioContext，則重新創建
 * @param {AudioContext} audioContext - 當前的 AudioContext
 * @param {Object} currentNodes - 當前的節點引用 { highFilter?, midFilter?, lowFilter?, gainNode? }
 * @param {Object} options - 配置選項
 * @param {number} options.volume - 音量值 (0-100)
 * @param {Object} options.eqValues - EQ 值 { high: number, mid: number, low: number }
 * @returns {Object|null} 包含所有節點的對象，如果 audioContext 無效則返回 null
 */
export const ensureAudioNodes = (audioContext, currentNodes, options = {}) => {
    if (!audioContext) {
        return null;
    }

    const { highFilter, midFilter, lowFilter, gainNode } = currentNodes || {};

    // 檢查現有節點是否屬於當前 AudioContext
    const needsRecreate = !highFilter || 
                         !midFilter || 
                         !lowFilter || 
                         !gainNode ||
                         highFilter.context !== audioContext ||
                         midFilter.context !== audioContext ||
                         lowFilter.context !== audioContext ||
                         gainNode.context !== audioContext;

    if (needsRecreate) {
        // 斷開舊節點的連接
        disconnectAudioNodes({ highFilter, midFilter, lowFilter, gainNode });

        // 重新創建節點
        return createEQChain(audioContext, options);
    }

    // 節點有效，返回現有節點
    return { highFilter, midFilter, lowFilter, gainNode };
};

/**
 * 初始化音頻系統
 * @param {string} audioPath - 音檔路徑
 * @param {Object} options - 配置選項
 * @param {number} options.volume - 音量值 (0-100)
 * @param {Object} options.eqValues - EQ 值 { high: number, mid: number, low: number }
 * @returns {Promise<Object>} 包含 audioContext, audioBuffer, 和節點的對象
 */
export const initializeAudio = async (audioPath, options = {}) => {
    try {
        // 創建 AudioContext
        const audioContext = createAudioContext();

        // 載入音檔
        const audioBuffer = await loadAudioFile(audioContext, audioPath);

        // 創建 EQ 鏈
        const nodes = createEQChain(audioContext, options);

        return {
            audioContext,
            audioBuffer,
            ...nodes
        };
    } catch (err) {
        console.error('Failed to initialize audio:', err);
        throw err;
    }
};

/**
 * 更新 EQ Filter 的增益值
 * @param {BiquadFilterNode} filter - Filter 節點
 * @param {number} value - 增益值 (0-100, 50 為中心值)
 */
export const updateEQGain = (filter, value) => {
    if (filter) {
        filter.gain.value = (value - 50) / 50 * 12;
    }
};

/**
 * 更新 Gain Node 的音量
 * @param {GainNode} gainNode - Gain Node
 * @param {number} volume - 音量值 (0-100)
 */
export const updateVolume = (gainNode, volume) => {
    if (gainNode) {
        gainNode.gain.value = volume / 100;
    }
};

